/* eslint-disable react/prop-types */ // TODO: add props
import { QUESTION_GROKKED } from 'edrolo/domains/exams';
import C3Chart from 'edrolo-style-guide/components/C3Chart';
import Legend from './Legend';
import styles from './styles.scss';

const colors = {
  'Understands': '#A8CB8C',
  'Needs help': '#EE7860',
  'Not reflected': '#dfdddd',
};

const getToolTipHTML = (datapoints, answers, answerOptions) => {
  const answerOptionIndex = datapoints[0].index;
  const answerOptionKey = answerOptions[answerOptionIndex];
  const description = answers[answerOptionKey];

  const tooltipItems = datapoints
    .filter((d) => d.value > 0)
    .map((d) => {
      const name = d.name;
      const value = d.value;
      return `
        <div id="tooltip-legend" style="background:${colors[name]};"></div>
        <span>${name}: ${value}</span><br/>`;
    });

  return tooltipItems.length > 0
    ? `
    <div id="the-tooltip">
      <div id="tooltip-description"><strong>${answerOptionKey}</strong>${description}</div>
      ${tooltipItems.join('')}
    </div>`
    : null;
};

export const StackedBarChartMultiChoice = ({
  question,
  solution,
  profileQuestionResponseSummary,
}) => {
  if (!question || !solution || !profileQuestionResponseSummary) {
    return <p>No Data</p>;
  }

  const correctAnswerId =
    solution
      .get('answers')
      .find((a) => a?.get('is_correct'))
      ?.get('id') || null; // return null if no correct answer provided

  const answers = question.get('answers').reduce(
    (accumulator, answer) => ({
      ...accumulator,
      [answer.get('serial')]: answer.get('text'),
    }),
    {}
  );

  const correctAnswer = question
    .get('answers')
    .filter((answer) => answer.get('id') === correctAnswerId)
    ?.first()
    ?.get('serial');

  const answerOptions = question
    .get('answers')
    .map((answer) => answer.get('serial'))
    .toJS(); // e.g. ['A', 'B', 'C', 'D'];

  const understandsByAnswerOption = ['Understands'].concat(
    answerOptions.map(
      (answerOption) =>
        profileQuestionResponseSummary.filter(
          (row) =>
            row.get('grokked') === QUESTION_GROKKED.YES &&
            row.get('answer') === answerOption
        ).size
    )
  );

  const needsHelpByAnswerOption = ['Needs help'].concat(
    answerOptions.map(
      (answerOption) =>
        profileQuestionResponseSummary.filter(
          (row) =>
            row.get('grokked') === QUESTION_GROKKED.NO &&
            row.get('answer') === answerOption
        ).size
    )
  );

  const notReflectedByAnswerOption = ['Not reflected'].concat(
    answerOptions.map(
      (answerOption) =>
        profileQuestionResponseSummary.filter(
          (row) =>
            row.get('grokked') === QUESTION_GROKKED.NO_RESPONSE &&
            row.get('answer') === answerOption
        ).size
    )
  );

  const correctAnswerIndex = answerOptions.indexOf(correctAnswer);
  const format = {
    'Understands': (v, id, i, j) => (i == correctAnswerIndex ? `✔` : ''),
  };

  const min = 0;
  const max = profileQuestionResponseSummary.size;
  const height = 180;
  const width = 366;

  const data = {
    columns: [
      notReflectedByAnswerOption, // e.g. ['Not reflected', 2, 0, 0, 0],
      needsHelpByAnswerOption,
      understandsByAnswerOption,
    ],
    type: 'bar',
    colors,
    groups: [['Not reflected', 'Needs help', 'Understands']],
    labels: {
      format,
    },
    order: null,
  };

  const options = {
    axis: {
      x: {
        type: 'category',
        categories: answerOptions,
        tick: {
          outer: false,
        },
      },
      y: {
        min,
        max,
        padding: {
          bottom: 0,
          top: 50, // leave room for the checkmark above correct bar if all students got the question correct.
        },
        tick: {
          values: [min, Math.floor(max / 2), max],
          outer: false,
        },
      },
    },

    grid: {
      y: {
        lines: [{ value: 0 }],
      },
    },

    size: {
      height,
      width,
    },

    legend: {
      show: false,
    },

    tooltip: {
      show: true,
      position: function (data, width, height, element) {
        return { bottom: 0, left: 20 };
      },
      grouped: true,
      horizontal: false,
      contents: function (datapoints, defaultTitleFormat, defaultValueFormat) {
        return getToolTipHTML(datapoints, answers, answerOptions);
      },
    },
  };

  return (
    <div className={styles.container}>
      <p className={styles.chartYAxisLabel}>No. of students</p>
      <C3Chart
        data={data}
        options={options}
        className={styles.chartContainer}
      />
      <Legend />
    </div>
  );
};
